import React, { useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { IoCheckmarkCircleOutline, IoCloseOutline, IoDownloadOutline, IoFilterOutline, IoWarningOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { exportCsv } from '../../../utilities/common';
import { formatUserName } from '../../reports/reportHelper';

const ViewDataSetModal = ({ show, onClose, dataset }) => {
  const { t } = useTranslation();
  const [section, setSection] = useState('all');
  const [user, setUser] = useState('all');
  const [status, setStatus] = useState('all');
  const fields = useMemo(() => dataset ?? [], [dataset]);
  const sections = useMemo(() => [...new Set(fields.map((field) => field.section))], [fields]);
  const users = useMemo(() => [...new Set(fields.map((field) => field.lastUpdatedUser).filter(Boolean))], [fields]);
  const filteredData = useMemo(() => fields.filter((field) => (
    (section === 'all' || field.section === section)
    && (user === 'all' || field.lastUpdatedUser?.id === user)
    && (status === 'all' || field.status === status)
  )), [fields, section, user, status]);

  const handleExport = () => {
    const rows = filteredData.map((field) => [
      field.section,
      field.dataField,
      formatUserName(field.lastUpdatedUser),
      field.lastUpdatedDate ? new Date(field.lastUpdatedDate).toLocaleDateString() : '',
      field.status === 'complete' ? t('Complete') : t('Missing'),
    ]);
    rows.unshift([
      t('Section'),
      t('Data Field'),
      t('Last Updated User'),
      t('Last Updated Date'),
      t('Status'),
    ]);
    exportCsv(rows, 'carbonview-dataset.csv');
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered dialogClassName="narrative-large-modal">
      <Modal.Header>
        <div>
          <Modal.Title>{t('CarbonView Data Set')}</Modal.Title>
          <div className="text-muted">{t('The following data set outlines the update statistics corresponding to each data field in the report')}</div>
        </div>
        <div>
          <Button variant="primary" className="mr-2" onClick={handleExport}>
            <IoDownloadOutline className="mr-1" />
            {t('Export to Excel')}
          </Button>
          <Button variant="link" onClick={onClose}><IoCloseOutline size={24} /></Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!dataset ? (
          <div className="text-center p-4"><Spinner animation="border" size="sm" /></div>
        ) : (
          <>
            <div className="narrative-filter-bar mb-3">
              <IoFilterOutline className="mr-2" />
              <strong className="mr-3">{t('Filters:')}</strong>
              <Form.Control as="select" value={section} onChange={(event) => setSection(event.target.value)} className="mr-2">
                <option value="all">{t('All Sections')}</option>
                {sections.map((item) => <option key={item} value={item}>{item}</option>)}
              </Form.Control>
              <Form.Control as="select" value={user} onChange={(event) => setUser(event.target.value)} className="mr-2">
                <option value="all">{t('All Users')}</option>
                {users.map((item) => <option key={item.id} value={item.id}>{formatUserName(item)}</option>)}
              </Form.Control>
              <Form.Control as="select" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="all">{t('All Statuses')}</option>
                <option value="complete">{t('Complete')}</option>
                <option value="missing">{t('Missing')}</option>
              </Form.Control>
            </div>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>{t('Section')}</th>
                  <th>{t('Data Field')}</th>
                  <th>{t('Last Updated User')}</th>
                  <th>{t('Last Updated Date')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((field) => (
                  <tr key={field.id}>
                    <td>{field.section}</td>
                    <td>{field.dataField}</td>
                    <td>{formatUserName(field.lastUpdatedUser)}</td>
                    <td>{new Date(field.lastUpdatedDate).toLocaleDateString()}</td>
                    <td>
                      <Badge variant={field.status === 'complete' ? 'success' : 'danger'}>
                        {field.status === 'complete'
                          ? <><IoCheckmarkCircleOutline className="mr-1" />{t('Complete')}</>
                          : <><IoWarningOutline className="mr-1" />{t('Missing')}</>}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViewDataSetModal;
